import type { ArtifactCardContent } from '@/types/ui-blocks';
import { artifactSlotId } from './ArtifactCanvasSlot';

/**
 * The artifact a card opens. The tool asks for `artifact_id`; a model copying
 * `artifact.id` out of the tool result sends `id` instead, and the card is a
 * receipt for a page that exists either way, so both are read.
 *
 * @param content - The card's content.
 *
 * @returns The id, or an empty string when the card names none.
 */
export function artifactIdOf(content: ArtifactCardContent): string {
  const raw = content.artifact_id ?? content.id;

  return raw === undefined || raw === null ? '' : String(raw);
}

/**
 * The version as a number. It is sent verbatim from a tool result, so it can
 * arrive as a string; anything unreadable is treated as absent rather than
 * shown as a badge saying NaN.
 *
 * @param raw - The version as the card carries it.
 *
 * @returns The version, or null.
 */
export function versionOf(raw: number | string | null | undefined): number | null {
  const parsed = typeof raw === 'string' ? Number(raw) : raw;

  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
}

/**
 * The canvas slot a card opens, or null when the card names no artifact.
 *
 * @param content - The card's content.
 *
 * @returns The slot id.
 */
export function artifactCardSlotId(content: ArtifactCardContent): string | null {
  const id = artifactIdOf(content);

  return id ? artifactSlotId(id, versionOf(content.version)) : null;
}
